/**
 * API Service for communicating with the QuillKit backend
 * Handles API key validation and feature checking
 */

// Default API base URL - uses production API server for validation
// This ensures API keys are validated against the actual key server, not the local server
// The value is replaced at build time by webpack DefinePlugin with the configured API_BASE_URL
// Can be overridden via the apiBaseUrl configuration option
const DEFAULT_API_BASE_URL = typeof process !== 'undefined' && process.env && process.env.API_BASE_URL
  ? process.env.API_BASE_URL
  : 'https://quillkit.com/api';

/**
 * Validate an API key with the backend
 * @param {string} apiKey - The API key to validate
 * @param {Object} options - Configuration options
 * @param {string} options.apiBaseUrl - Base URL for the API
 * @returns {Promise<Object>} Validation result with features and plan info
 */
export async function validateApiKey(apiKey, options = {}) {
  const apiBaseUrl = options.apiBaseUrl || DEFAULT_API_BASE_URL;
  const endpoint = `${apiBaseUrl}/keys/validate`;

  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': apiKey
      },
      body: JSON.stringify({ apiKey })
    });

    const data = await response.json();

    if (!response.ok) {
      console.log('API key validation failed.');
      // Return validation failure with details
      return {
        valid: false,
        error: data.error || 'Validation failed',
        message: data.message || 'Unable to validate API key',
        status: response.status,
        details: data.details || null
      };
    }

    // Return successful validation result
    console.log('API key validated successfully.');
    return {
      valid: true,
      plan: data.plan,
      features: data.features || [],
      usage: data.usage || {},
      expiresAt: data.expiresAt,
      metadata: data.metadata || {}
    };
  } catch (error) {
    // Network error or other failure
    console.warn('API key validation request failed:', error);
    return {
      valid: false,
      error: 'Network error',
      message: 'Unable to connect to the API server.',
      networkError: true,
      originalError: error.message
    };
  }
}

/**
 * Check if a specific feature is available for the given API key
 * @param {string} apiKey - The API key
 * @param {string} feature - Feature name to check
 * @param {Object} options - Configuration options
 * @returns {Promise<boolean>} True if feature is available
 */
export async function hasFeature(apiKey, feature, options = {}) {
  const validationResult = await validateApiKey(apiKey, options);

  if (!validationResult.valid) {
    return false;
  }

  return validationResult.features.includes(feature);
}

export default {
  validateApiKey,
  hasFeature
};
